const React = require('react');
const { createElement: h, useState, useEffect, useRef } = React;
const { request } = require('../lib/apiClient');
const SparkleIcon = require('./SparkleIcon');
require('./AgentConfiguratorModal.css');

function normalizeType(t) {
  var s = String(t || '').toLowerCase();
  if (s === 'string' || s === 'text') return 'string';
  if (s === 'int' || s === 'integer' || s === 'number') return 'int';
  if (s === 'bool' || s === 'boolean') return 'bool';
  if (s === 'date' || s === 'datetime') return 'date';
  if (s === 'object' || s === 'json') return 'object';
  if (s === 'array' || s === 'list') return 'array';
  return 'string';
}

var rtIdCounter = 0;
function newRtId() { return 'rt-' + (++rtIdCounter) + '-' + Date.now(); }

function AgentConfiguratorModal(props) {
  var onConfirm = props.onConfirm;
  var onCancel = props.onCancel;
  var variableNodes = props.variableNodes || [];

  var [prompt, setPrompt] = useState('');
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState('');
  var [result, setResult] = useState(null);
  var textareaRef = useRef(null);

  useEffect(function() {
    if (textareaRef.current) textareaRef.current.focus();
  }, []);

  // Build variable context array to send to AI
  function buildVarContext() {
    return variableNodes
      .filter(function(vn) { return String(vn.title || '').trim(); })
      .map(function(vn) {
        var params = vn.paramValues || {};
        return {
          name: String(vn.title || '').trim(),
          type: normalizeType(params.dataType),
          value: params.value !== undefined && params.value !== '' ? String(params.value) : null
        };
      });
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') onCancel();
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && prompt.trim() && !loading) configure();
  }

  function configure() {
    var p = prompt.trim();
    if (!p) {
      if (textareaRef.current) textareaRef.current.focus();
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    request('/ai/configure-agent', {
      method: 'POST',
      body: { prompt: p, variables: buildVarContext() }
    })
      .then(function(data) {
        setResult({
          title: data.title || 'AI',
          system: data.system || 'You are a helpful AI assistant.',
          temperature: typeof data.temperature === 'number' ? data.temperature : 0.7,
          variableInputs: Array.isArray(data.variableInputs) ? data.variableInputs : [],
          responseTemplate: Array.isArray(data.responseTemplate)
            ? data.responseTemplate.map(function(f) {
                if (f && typeof f === 'object') return { id: newRtId(), name: String(f.name || ''), description: String(f.description || '') };
                return { id: newRtId(), name: String(f || ''), description: '' };
              })
            : [],
          _showVarPicker: false
        });
        setLoading(false);
      })
      .catch(function(err) {
        setError(err.message || 'Failed to configure. Please try again.');
        setLoading(false);
      });
  }

  function handleConfirm() {
    if (result) {
      onConfirm({
        title: result.title,
        system: result.system,
        temperature: result.temperature,
        variableInputs: result.variableInputs || [],
        responseTemplate: (result.responseTemplate || [])
          .filter(function(k) { return k.name.trim(); })
          .map(function(k) { return { name: k.name.trim(), description: (k.description || '').trim() }; })
      });
    } else if (prompt.trim()) {
      onConfirm({ title: 'AI', system: prompt.trim(), temperature: 0.7, variableInputs: [], responseTemplate: [] });
    }
  }

  return h('div', { className: 'acm-overlay', onMouseDown: function(e) { if (e.target === e.currentTarget) onCancel(); } },
    h('div', { className: 'acm-modal', onKeyDown: handleKeyDown, onMouseDown: function(e) { e.stopPropagation(); } },

      h('div', { className: 'acm-header' },
        h('div', { className: 'acm-header-icon' },
          h(SparkleIcon, { className: 'acm-header-icon-svg', size: 18 })
        ),
        h('div', null,
          h('div', { className: 'acm-title' }, 'Configure AI Node'),
          h('div', { className: 'acm-subtitle' }, 'Describe the role — workflow variables will be sent as context')
        ),
        h('button', { className: 'acm-close', onClick: onCancel }, '\u00d7')
      ),

      h('div', { className: 'acm-body' },
        h('div', { className: 'acm-field' },
          h('label', { className: 'acm-label' }, 'What should this AI do?'),
          h('textarea', {
            ref: textareaRef,
            className: 'acm-textarea',
            rows: 3,
            placeholder: 'e.g. "Write a personalized email using the @firstname and @email variables" or "Summarize customer feedback and extract action items"',
            value: prompt,
            onChange: function(e) { setPrompt(e.target.value); setResult(null); setError(''); },
            onKeyDown: handleKeyDown,
            disabled: loading
          })
        ),

        h('button', {
          className: 'acm-configure-btn',
          onClick: configure,
          disabled: loading
        },
          loading
            ? h('span', { className: 'acm-spinner' })
            : h('span', { className: 'acm-btn-icon', 'aria-hidden': 'true' },
                h(SparkleIcon, { className: 'acm-btn-icon-svg', size: 15 })
              ),
          loading ? 'Configuring...' : 'Configure with AI'
        ),

        error ? h('div', { className: 'acm-error' }, error) : null,

        result
          ? h('div', { className: 'acm-result' },
              h('div', { className: 'acm-result-header' },
                h('span', { className: 'acm-result-label' }, 'Generated Configuration'),
                h('span', { className: 'acm-result-badge' }, 'AI')
              ),

              // Name
              h('div', { className: 'acm-result-field' },
                h('span', { className: 'acm-result-key' }, 'Name'),
                h('input', {
                  className: 'acm-result-input',
                  value: result.title,
                  onChange: function(e) { setResult(Object.assign({}, result, { title: e.target.value })); }
                })
              ),

              // System Prompt
              h('div', { className: 'acm-result-field' },
                h('span', { className: 'acm-result-key' }, 'System Prompt'),
                h('textarea', {
                  className: 'acm-result-textarea',
                  rows: 4,
                  value: result.system,
                  onChange: function(e) { setResult(Object.assign({}, result, { system: e.target.value })); }
                })
              ),

              // Temperature
              h('div', { className: 'acm-result-field acm-result-field-row' },
                h('span', { className: 'acm-result-key' }, 'Creativity Level'),
                h('input', {
                  className: 'acm-result-input acm-result-input-small',
                  type: 'number', min: '0', max: '1', step: '0.1',
                  value: result.temperature,
                  onChange: function(e) { setResult(Object.assign({}, result, { temperature: parseFloat(e.target.value) || 0.7 })); }
                })
              ),

              // Response Template
              h('div', { className: 'acm-result-field' },
                h('div', { className: 'acm-result-vars-header' },
                  h('span', { className: 'acm-result-key' }, 'Output Fields'),
                  h('button', {
                    className: 'acm-result-vars-add',
                    onClick: function() {
                      setResult(Object.assign({}, result, {
                        responseTemplate: result.responseTemplate.concat([{ id: newRtId(), name: '', description: '' }])
                      }));
                    }
                  }, '+ Add Field')
                ),
                h('div', { className: 'acm-result-template' },
                  result.responseTemplate.length === 0
                    ? h('span', { className: 'acm-result-vars-empty' }, 'No output fields — AI will return raw text.')
                    : result.responseTemplate.map(function(k) {
                        return h('div', { key: k.id, className: 'acm-result-template-row' },
                          h('span', { className: 'acm-result-template-dot' }),
                          h('div', { className: 'acm-result-template-field' },
                            h('input', {
                              className: 'acm-result-template-input',
                              type: 'text',
                              placeholder: 'field_name',
                              value: k.name,
                              onChange: function(e) {
                                setResult(Object.assign({}, result, {
                                  responseTemplate: result.responseTemplate.map(function(f) {
                                    return f.id === k.id ? { id: f.id, name: e.target.value, description: f.description } : f;
                                  })
                                }));
                              }
                            }),
                            h('input', {
                              className: 'acm-result-template-desc',
                              type: 'text',
                              placeholder: 'description (optional)',
                              value: k.description || '',
                              onChange: function(e) {
                                setResult(Object.assign({}, result, {
                                  responseTemplate: result.responseTemplate.map(function(f) {
                                    return f.id === k.id ? { id: f.id, name: f.name, description: e.target.value } : f;
                                  })
                                }));
                              }
                            })
                          ),
                          h('button', {
                            className: 'acm-result-var-remove',
                            onClick: function() {
                              setResult(Object.assign({}, result, {
                                responseTemplate: result.responseTemplate.filter(function(f) { return f.id !== k.id; })
                              }));
                            }
                          }, '\u00d7')
                        );
                      })
                )
              )
            )
          : null
      ),

      h('div', { className: 'acm-actions' },
        h('button', { className: 'acm-btn acm-btn-secondary', onClick: onCancel }, 'Cancel'),
        h('button', {
          className: 'acm-btn acm-btn-primary',
          onClick: handleConfirm,
          disabled: !result && !prompt.trim()
        }, 'Add Node')
      )
    )
  );
}

module.exports = AgentConfiguratorModal;
